import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { useMe } from "@/lib/hooks/use-me";
import { startOfDayInTz } from "@/utils/format-datetime";

export function useMyApplications(projectId?: string, options?: { fromStartOfDay?: boolean }) {
  const { me } = useMe();
  const ITEMS_PER_PAGE = 10;

  // Calculate the start date based on options
  //
  // `setHours(0, 0, 0, 0)` zet de klok op middernacht van het TOESTEL. Op een
  // telefoon westelijk van Greenwich lag die middernacht midden in de
  // Amsterdamse ochtend, waardoor aanmeldingen van eerder vandaag uit de lijst
  // vielen.
  const getStartDate = () => {
    if (options?.fromStartOfDay) {
      return startOfDayInTz().toISOString();
    }
    return new Date().toISOString();
  };

  const { data, fetchNextPage, hasNextPage, refetch, isLoading } = useInfiniteQuery({
    queryKey: [
      "applications",
      me?.employee?.id,
      ITEMS_PER_PAGE,
      projectId,
      options?.fromStartOfDay,
    ],
    queryFn: async (args) => {
      const res = await api.application.getAll({
        pagination: {
          take: ITEMS_PER_PAGE,
          page: args.pageParam + 1,
        },
        filter: {
          ...(projectId && {
            "availabilitySlot.project.id": {
              value: projectId,
              operator: "equals",
            },
          }),
          "employee.id": {
            value: me?.employee?.id,
            operator: "equals",
          },
          "availabilitySlot.startAt": {
            value: getStartDate(),
            operator: "gte",
          },
        },
        include: {
          availabilitySlot: {
            select: true,
            project: {
              select: true,
            },
            userFunction: {
              select: true,
            },
            position: {
              select: true,
            },
          },
        },
        sort: {
          field: "availabilitySlot.startAt",
          order: "asc",
        },
      });
      return res.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalItems = lastPage.totalAmount;
      const currentItems = (lastPageParam + 1) * ITEMS_PER_PAGE;

      if (currentItems >= totalItems) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      return firstPageParam - 1;
    },
    enabled: !!me?.employee?.id,
  });

  const loadMore = async (ev: CustomEvent) => {
    await fetchNextPage();
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  const applications =
    data?.pages.flatMap((page) => page.items).filter((application) => application !== undefined) ??
    [];

  return {
    applications,
    loadMore,
    hasNextPage,
    refetch,
    isLoading,
  };
}
